import { Box } from "@mui/material";
import React, { forwardRef, useMemo } from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { formatFullWeekdayShortDate } from "../../../../../../../common/utils/date";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { dayEntries } from "../../helpers/activitySelectors";
import { ScheduledService, WorkoutDay } from "../../model/workoutPlanTypes";
import { useWorkoutPlan } from "../../store/useWorkoutPlan";
import ServiceCard from "../cards/ServiceCard";
import TrackedActivityCard from "../cards/TrackedActivityCard";
import WorkoutCard from "../cards/WorkoutCard";

type Props = {
  day: WorkoutDay;
  services: ScheduledService[];
  isToday: boolean;
};

/**
 * One day of the week, ported from the mobile `DateCard` + its entries: the
 * date, a Today pill and the day's calories against the target, then the
 * day's cards two across, wrapping — planned workouts first, then wearable
 * activities, then coached services (a Rest Day is hidden when a wearable
 * activity was recorded). Picking a card selects it in the store; the
 * details pane beside the week shows it.
 */
const WeekDayRow = forwardRef<HTMLDivElement, Props>(
  ({ day, services, isToday }, ref) => {
    const theme: Theme = useTheme();
    const { details, openDetails } = useWorkoutPlan();
  const isSelected = (kind: "workout" | "event" | "service", id: string) =>
    details?.kind === kind && details.id === id && details.date === day.day;
    const entries = useMemo(() => dayEntries(day, services), [day, services]);

    return (
      <Box ref={ref} sx={styles.row(theme, isToday)}>
        <Box sx={styles.header}>
          <Box sx={styles.title(theme)}>{formatFullWeekdayShortDate(day.day)}</Box>
          {isToday && <Box sx={styles.todayPill(theme)}>Today</Box>}
          {/* Rest days carry a 0 target; a "0 / 0 kcal" line says nothing. */}
          {(day.cals?.target > 0 || day.cals?.current > 0) && (
            <Box sx={styles.kcal(theme)}>
              {Math.round(day.cals.current)} / {Math.round(day.cals.target)} kcal
            </Box>
          )}
        </Box>
        {(entries.scheduled.length > 0 ||
          entries.events.length > 0 ||
          entries.services.length > 0) && (
          <Box sx={styles.cards}>
            {entries.scheduled.map((activity) => (
              <Box key={activity.id} sx={styles.cardSlot}>
                <WorkoutCard
                  activity={activity}
                  selected={isSelected("workout", activity.id)}
                  onOpen={(item) =>
                    openDetails({ kind: "workout", id: item.id, date: day.day })
                  }
                />
              </Box>
            ))}
            {entries.events.map((event) => (
              <Box key={event.id} sx={styles.cardSlot}>
                <TrackedActivityCard
                  event={event}
                  selected={isSelected("event", event.id)}
                  onOpen={(item) =>
                    openDetails({ kind: "event", id: item.id, date: day.day })
                  }
                />
              </Box>
            ))}
            {entries.services.map((service) => (
              <Box key={service.id} sx={styles.cardSlot}>
                <ServiceCard
                  service={service}
                  selected={isSelected("service", service.id)}
                  onOpen={(item) =>
                    openDetails({ kind: "service", id: item.id, date: day.day })
                  }
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  }
);

WeekDayRow.displayName = "WeekDayRow";

export default WeekDayRow;

const styles = {
  row: (theme: Theme, isToday: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "12px",
    padding: "16px",
    borderRadius: "16px",
    border: `1px solid ${
      isToday ? theme.colorTokens.P[60] : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  todayPill: (theme: Theme) => ({
    padding: "2px 8px",
    borderRadius: "12px",
    backgroundColor: theme.colorTokens.P[10],
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
  }),
  kcal: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  // Two cards fill a row; a day with more wraps them so every card stays visible.
  cards: {
    display: "flex",
    flexWrap: "wrap" as "wrap",
    gap: "16px",
  },
  cardSlot: {
    flex: "0 0 calc((100% - 16px) / 2)",
    minWidth: 0,
    display: "flex",
    // minWidth 0: a long nowrap title must truncate, not widen the card past its slot.
    "& > *": { flex: 1, minWidth: 0 },
  },
};
